import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { AlertTriangle, Check, Loader2, Minus, Plus, Search, ShoppingCart, Undo2 } from "lucide-react";
import { api, formatApiError } from "../../api";
import { DESK } from "../../constants/testIds";
import { useDeskData } from "../../contexts/DeskDataContext";
import { formatCurrency } from "../../lib/format";
import { fadeUp } from "../../lib/motion";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { StatusPill } from "../../components/common/StatusPill";
import { toast } from "../../components/ui/sonner";
import { useCelebration } from "../../contexts/CelebrationContext";

// UX-FD-02 §1 SCR-P02-40: Sell + Refund share one page (two tabs) so FD never leaves the
// register mid-shift. Refund needs `retail.manage` — Manager-tier only on the existing backend
// (FD's default role only has `retail.view`+`sale.create`) — the Refund tab only renders if
// `me.permissions` actually has it (server-enforced + absent, not disabled). A concurrent
// last-unit sale returns 409 INSUFFICIENT_STOCK — caught specifically below and shown as a
// calm "just sold out" message, never a raw error (checklist item 1).
export default function Retail() {
  const { branchId, me, readOnlyLapse } = useDeskData();
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const { celebrate } = useCelebration();
  const [tab, setTab] = useState("sell");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [mode, setMode] = useState("cash");
  const [railRef, setRailRef] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [soldOut, setSoldOut] = useState(null);
  const canRefund = me?.permissions?.includes("retail.manage");

  const productsQ = useQuery({ queryKey: ["desk-products"], queryFn: async () => (await api.get("/products")).data.products });
  const stockQ = useQuery({
    queryKey: ["desk-stock", branchId],
    queryFn: async () => (await api.get(`/stock/${branchId}`)).data.stock,
    enabled: !!branchId,
  });
  const salesQ = useQuery({
    queryKey: ["desk-sales", branchId],
    queryFn: async () => (await api.get("/sales", { params: { branch_id: branchId } })).data.sales,
    enabled: tab === "refund",
  });

  const stockFor = (productId) => stockQ.data?.find((s) => s.product_ref === productId);
  const filteredProducts = useMemo(
    () => (productsQ.data || []).filter((p) => p.name.toLowerCase().includes(search.toLowerCase())),
    [productsQ.data, search],
  );

  const addToCart = (product) => {
    setCart((prev) => {
      const existing = prev.find((l) => l.product_ref === product.id);
      if (existing) return prev.map((l) => (l.product_ref === product.id ? { ...l, qty: l.qty + 1 } : l));
      return [...prev, { product_ref: product.id, name: product.name, price: product.price, qty: 1 }];
    });
  };
  const changeQty = (productId, delta) => {
    setCart((prev) => prev
      .map((l) => (l.product_ref === productId ? { ...l, qty: l.qty + delta } : l))
      .filter((l) => l.qty > 0));
  };
  const cartTotal = cart.reduce((sum, l) => sum + l.price * l.qty, 0);

  const checkoutMutation = useMutation({
    mutationFn: () => api.post(
      "/sales",
      { branch_id: branchId, member_ref: null, lines: cart.map((l) => ({ product_ref: l.product_ref, qty: l.qty })), payment_mode: mode, rail_ref: mode === "digital" ? railRef : null },
      { headers: { "Idempotency-Key": crypto.randomUUID() } },
    ),
    onSuccess: (res) => {
      setReceipt({ ...res.data, cartSnapshot: cart });
      setCart([]); setRailRef(""); setSoldOut(null);
      queryClient.invalidateQueries({ queryKey: ["desk-stock"] });
    },
    onError: (e) => {
      const data = e.response?.data;
      if (data?.code === "INSUFFICIENT_STOCK") {
        setSoldOut(data.message);
      } else {
        toast.error(formatApiError(e).message);
      }
    },
  });

  const refundMutation = useMutation({
    mutationFn: ({ saleId, reason, restock }) => api.post(
      `/sales/${saleId}/refund`, { reason, restock },
      { headers: { "Idempotency-Key": crypto.randomUUID() } },
    ),
    onSuccess: () => {
      celebrate("refund_processed");
      queryClient.invalidateQueries({ queryKey: ["desk-sales"] });
      queryClient.invalidateQueries({ queryKey: ["desk-stock"] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <div data-testid={DESK.retailPage} className="space-y-6">
      <motion.div {...fadeUp(reducedMotion)} className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Retail</h1>
        <div className="flex gap-2">
          <Button size="sm" data-testid={DESK.retailTab("sell")} variant={tab === "sell" ? "default" : "outline"} onClick={() => setTab("sell")} className={tab === "sell" ? "text-white" : ""} style={tab === "sell" ? { background: "#D6202B" } : undefined}>Sell</Button>
          {canRefund && (
            <Button size="sm" data-testid={DESK.retailTab("refund")} variant={tab === "refund" ? "default" : "outline"} onClick={() => setTab("refund")} className={tab === "refund" ? "text-white" : ""} style={tab === "refund" ? { background: "#D6202B" } : undefined}>Refund</Button>
          )}
        </div>
      </motion.div>

      {tab === "sell" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <motion.div {...fadeUp(reducedMotion, 0.03)} className="space-y-3">
            <div className="flex gap-2">
              <Input data-testid={DESK.retailProductSearchInput} placeholder="Search products…" value={search} onChange={(e) => setSearch(e.target.value)} />
              <Search size={18} className="mt-2.5 shrink-0" style={{ color: "#68686E" }} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {filteredProducts.map((p) => {
                const stock = stockFor(p.id);
                const outOfStock = stock && stock.quantity <= 0;
                return (
                  <button
                    key={p.id}
                    data-testid={DESK.retailProductCard(p.id)}
                    disabled={outOfStock}
                    onClick={() => addToCart(p)}
                    className="text-left rounded-[12px] p-3 disabled:opacity-40 hover:border-[#D6202B] transition-colors scarlet-focusable"
                    style={{ background: "#fff", border: "1px solid #EBEAE5" }}
                  >
                    <p className="font-semibold text-sm" style={{ color: "#141416" }}>{p.name}</p>
                    <p className="text-xs mt-0.5" style={{ color: "#68686E" }}>{formatCurrency(p.price)} · {outOfStock ? "Out of stock" : stock ? `${stock.quantity} in stock` : ""}</p>
                  </button>
                );
              })}
            </div>
          </motion.div>

          <motion.div {...fadeUp(reducedMotion, 0.06)} className="rounded-[14px] p-4 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
            <p className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "#141416" }}><ShoppingCart size={15} /> Cart</p>
            {cart.length === 0 && <p className="text-sm" style={{ color: "#68686E" }}>No items yet — tap a product to add it.</p>}
            {cart.map((l) => (
              <div key={l.product_ref} data-testid={DESK.retailCartItem(l.product_ref)} className="flex items-center justify-between text-sm">
                <span style={{ color: "#141416" }}>{l.name}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => changeQty(l.product_ref, -1)}><Minus size={13} /></button>
                  <span className="w-5 text-center">{l.qty}</span>
                  <button onClick={() => changeQty(l.product_ref, 1)}><Plus size={13} /></button>
                  <span className="font-semibold w-16 text-right">{formatCurrency(l.price * l.qty)}</span>
                </div>
              </div>
            ))}
            {soldOut && (
              <p data-testid={DESK.retailSoldOutMessage} className="flex items-center gap-1.5 text-sm font-medium" style={{ color: "var(--scarlet-neg)" }}>
                <AlertTriangle size={14} /> {soldOut} — someone else just bought the last unit. Remove or adjust this item and try again.
              </p>
            )}
            {cart.length > 0 && (
              <>
                <div className="flex items-center justify-between font-bold text-sm pt-2" style={{ borderTop: "1px solid #EBEAE5", color: "#141416" }}>
                  <span>Total</span><span>{formatCurrency(cartTotal)}</span>
                </div>
                <Select onValueChange={setMode} value={mode}>
                  <SelectTrigger data-testid={DESK.retailPaymentMode}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="digital">Digital (UPI/Card)</SelectItem>
                  </SelectContent>
                </Select>
                {mode === "digital" && <Input data-testid={DESK.retailRailRefInput} placeholder="Transaction reference" value={railRef} onChange={(e) => setRailRef(e.target.value)} />}
                <Button
                  data-testid={DESK.retailCheckoutButton}
                  disabled={readOnlyLapse || checkoutMutation.isPending || (mode === "digital" && !railRef)}
                  onClick={() => checkoutMutation.mutate()}
                  className="w-full text-white gap-2" style={{ background: "#D6202B" }}
                >
                  {checkoutMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : `Charge ${formatCurrency(cartTotal)}`}
                </Button>
              </>
            )}
          </motion.div>
        </div>
      )}

      {tab === "sell" && receipt && (
        <motion.div {...fadeUp(reducedMotion)} data-testid={DESK.retailReceipt} className="rounded-[14px] p-5 max-w-sm" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <p className="flex items-center gap-2 font-semibold text-sm mb-2" style={{ color: "var(--scarlet-pos)" }}><Check size={16} /> Sale complete</p>
          {receipt.cartSnapshot.map((l) => (
            <div key={l.product_ref} className="flex justify-between text-xs py-0.5" style={{ color: "#68686E" }}>
              <span>{l.name} × {l.qty}</span><span>{formatCurrency(l.price * l.qty)}</span>
            </div>
          ))}
          <div className="flex justify-between font-bold text-sm pt-2 mt-1" style={{ borderTop: "1px solid #EBEAE5", color: "#141416" }}>
            <span>Total paid ({receipt.mode || mode})</span><span>{formatCurrency(receipt.total)}</span>
          </div>
        </motion.div>
      )}

      {tab === "refund" && canRefund && (
        <motion.div {...fadeUp(reducedMotion, 0.03)} className="rounded-[14px] overflow-hidden" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          {salesQ.data?.length === 0 && <p className="p-4 text-sm" style={{ color: "#68686E" }}>No sales recorded yet.</p>}
          {salesQ.data?.map((s) => <RefundRow key={s.id} sale={s} onRefund={(payload) => refundMutation.mutate(payload)} busy={refundMutation.isPending} />)}
        </motion.div>
      )}
    </div>
  );
}

function RefundRow({ sale, onRefund, busy }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [restock, setRestock] = useState(true);
  const alreadyRefunded = sale.status === "refunded";

  return (
    <div data-testid={DESK.retailSaleRow(sale.id)} className="px-4 py-3" style={{ borderBottom: "1px solid #EBEAE5" }}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium" style={{ color: "#141416" }}>{sale.lines.length} item(s) — {formatCurrency(sale.total)}</p>
          <p className="text-xs" style={{ color: "#68686E" }}>{new Date(sale.at).toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={alreadyRefunded ? "overdue" : "active"} label={sale.status} />
          {!alreadyRefunded && (
            <Button size="sm" variant="outline" data-testid={DESK.retailRefundButton(sale.id)} onClick={() => setOpen((v) => !v)} className="gap-1"><Undo2 size={13} /> Refund</Button>
          )}
        </div>
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          <Input data-testid={DESK.retailRefundReasonInput} placeholder="Reason for refund (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <label className="flex items-center gap-2 text-sm" style={{ color: "#141416" }}>
            <Checkbox data-testid={DESK.retailRefundRestockCheckbox} checked={restock} onCheckedChange={setRestock} /> Restock these items
          </label>
          <Button
            size="sm" data-testid={DESK.retailRefundSubmit} disabled={!reason.trim() || busy}
            onClick={() => onRefund({ saleId: sale.id, reason, restock })}
            className="text-white" style={{ background: "var(--scarlet-neg)" }}
          >
            Confirm refund
          </Button>
        </div>
      )}
    </div>
  );
}
